import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Copy,
  Check,
  Info,
  Plus,
  MessageCircle,
  Edit,
  Trash2,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { DeleteConfirmationModal } from '@/components/modals/DeleteConfirmationModal';
import { useState } from 'react';
import type { ChatWidget } from '@/services/chatIntegration';
import { chatIntegrationAPI } from '@/services/chatIntegration';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';

const ChatIntegration = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    data: chatWidgets = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<ChatWidget[]>({
    queryKey: ['chatWidgets'],
    queryFn: chatIntegrationAPI.getChatWidgets,
  });

  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [copiedScriptType, setCopiedScriptType] = useState<string | null>(null);
  const [deletingWidgetId, setDeletingWidgetId] = useState<number | null>(null);
  const [updatingWidgetId, setUpdatingWidgetId] = useState<number | null>(null);

  // Generate script with only non-false props
  const getScript = (widget: ChatWidget) => {
    const config: Record<string, unknown> = {
      widgetKey: widget.key,
      apiBase: 'https://admin-backend.findecor.io',
      socketUrl: 'wss://admin-backend.findecor.io',
    };

    // Add optional props only if they are not false/null/undefined
    if (widget.color) config.color = widget.color;
    if (widget.textColor) config.textColor = widget.textColor;
    if (widget.widgetSize) config.widgetSize = widget.widgetSize;
    if (widget.position) config.position = widget.position;
    if (widget.borderRadius) config.borderRadius = widget.borderRadius;
    if (widget.companyName) config.companyName = widget.companyName;
    if (widget.autoOpen !== undefined && widget.autoOpen !== false)
      config.autoOpen = widget.autoOpen;
    if (widget.autoDelay !== undefined && widget.autoDelay !== false)
      config.autoDelay = widget.autoDelay;
    if (
      widget.typingIndicator !== undefined &&
      widget.typingIndicator !== false
    )
      config.typingIndicator = widget.typingIndicator;
    if (
      widget.soundNotifications !== undefined &&
      widget.soundNotifications !== false
    )
      config.soundNotifications = widget.soundNotifications;
    if (widget.headerText) config.headerText = widget.headerText;
    if (widget.welcomeMessage) config.welcomeMessage = widget.welcomeMessage;
    if (widget.offlineMessage) config.offlineMessage = widget.offlineMessage;
    if (widget.inputPlaceholder)
      config.inputPlaceholder = widget.inputPlaceholder;
    if (widget.buttonText) config.buttonText = widget.buttonText;

    const configString = JSON.stringify(config, null, 2);

    return `<script src="https://cdn.jsdelivr.net/gh/Jobir03/test-widget@master/dist/findecor-chat-widget.umd.js"></script>
<script>
  window.FindecorChatWidget.init(${configString});
</script>`;
  };

  const copyToClipboard = (text: string, widgetId: number, type: string) => {
    navigator.clipboard.writeText(text.trim());
    setCopiedId(widgetId);
    setCopiedScriptType(type);
    setTimeout(() => {
      setCopiedId(null);
      setCopiedScriptType(null);
    }, 2000);
  };

  const handleSettings = (widgetId: number) => {
    console.log('Settings clicked for widget:', widgetId);
    navigate(`/integration/chat-integration/edit/${widgetId}`);
  };

  const handleAddWidget = () => {
    navigate(`/integration/chat-integration/add`);
  };

  const { mutateAsync: deleteWidget, isPending: isDeleting } = useMutation({
    mutationFn: (widgetId: number) =>
      chatIntegrationAPI.deleteChatWidget(widgetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatWidgets'] });
      toast.success('Widget deleted successfully');
      setDeletingWidgetId(null);
    },
    onError: (error: unknown) => {
      const message = (error as Error)?.message ?? 'Failed to delete widget';
      toast.error(message);
    },
  });

  const { mutateAsync: updateWidgetStatus } = useMutation({
    mutationFn: ({
      widgetId,
      isActive,
    }: {
      widgetId: number;
      isActive: boolean;
    }) => chatIntegrationAPI.updateWidgetStatus(widgetId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatWidgets'] });
      toast.success('Widget status updated successfully');
      setUpdatingWidgetId(null);
    },
    onError: (error: unknown) => {
      const message =
        (error as Error)?.message ?? 'Failed to update widget status';
      toast.error(message);
      setUpdatingWidgetId(null);
    },
  });

  const handleDeleteWidget = async () => {
    if (!deletingWidgetId) return;
    await deleteWidget(deletingWidgetId);
  };

  const handleToggleStatus = async (widget: ChatWidget) => {
    setUpdatingWidgetId(widget.id);
    await updateWidgetStatus({
      widgetId: widget.id,
      isActive: !widget.isActive,
    });
  };

  const handleOpenChat = (widgetKey: string) => {
    const url = `https://demo.findecor.io/${widgetKey}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Chat Widgets</h2>
          <Button onClick={handleAddWidget} className=" hover:bg-gray-800">
            <Plus className="h-4 w-4 mr-2" />
            Add New Widget
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <Card
              key={index}
              className="border overflow-hidden flex gap-2 rounded-2xl flex-col"
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Skeleton className="w-10 h-10 rounded-lg" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
              </CardHeader>
              <CardContent className="!p-3 space-y-2">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Chat Widgets</h2>
          <Button onClick={handleAddWidget} className=" hover:bg-gray-800">
            <Plus className="h-4 w-4 mr-2" />
            Add New Widget
          </Button>
        </div>
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>{(error as Error)?.message || 'Failed to load widgets'}</span>
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              <Loader2 className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Chat Widgets</h2>
        <Button onClick={handleAddWidget} className=" hover:bg-gray-800">
          <Plus className="h-4 w-4 mr-2" />
          Add New Widget
        </Button>
      </div>
      {chatWidgets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MessageCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No widgets found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Get started by creating your first chat widget
            </p>
            <Button onClick={handleAddWidget}>
              <Plus className="h-4 w-4 mr-2" />
              Create Widget
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {chatWidgets.map((widget) => (
            <Card
              key={widget.id}
              className="border  overflow-hidden flex gap-2 rounded-2xl flex-col"
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: widget.color }}
                    >
                      <MessageCircle
                        className="h-5 w-5"
                        style={{ color: '#ffffff' }}
                      />
                    </div>
                    <div>
                      <CardTitle className="text-base font-medium">
                        {widget.companyName || widget.name || 'Chat Widget'}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">
                        {widget.createdAt
                          ? `Created ${new Date(widget.createdAt).toLocaleDateString()}`
                          : 'Unknown date'}
                      </p>
                    </div>
                  </div>
                  <Badge variant={widget.isActive ? 'default' : 'destructive'}>
                    {widget.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="!p-3 space-y-2 flex-grow overflow-y-auto">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Info className="h-3 w-3 flex-shrink-0" />
                      <span>
                        Add to HTML{' '}
                        <code className="bg-gray-100 px-1 rounded">
                          &lt;head&gt;
                        </code>{' '}
                        section:
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-xs gap-1"
                      onClick={() =>
                        copyToClipboard(
                          '<link rel="stylesheet" href="https://cdn.jsdelivr.net/gh/Jobir03/test-widget@master/dist/findecor-chat-widget.css" />',
                          widget.id,
                          'css'
                        )
                      }
                    >
                      {copiedId === widget.id && copiedScriptType === 'css' ? (
                        <Check className="h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                      Copy
                    </Button>
                  </div>
                  <div className="bg-gray-50 p-3 rounded text-xs overflow-x-auto">
                    <pre className="whitespace-pre-wrap break-words">
                      {`<link rel="stylesheet" href="https://cdn.jsdelivr.net/gh/Jobir03/test-widget@master/dist/findecor-chat-widget.css" />`}
                    </pre>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      <span>
                        Add before the closing{' '}
                        <code className="bg-gray-100 px-1 rounded">
                          &lt;/body&gt;
                        </code>{' '}
                        tag:
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-xs gap-1"
                      onClick={() =>
                        copyToClipboard(getScript(widget), widget.id, 'script')
                      }
                    >
                      {copiedId === widget.id &&
                      copiedScriptType === 'script' ? (
                        <Check className="h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                      Copy
                    </Button>
                  </div>
                  <div className="bg-gray-50 p-3 rounded text-xs overflow-x-auto">
                    <pre className="whitespace-pre-wrap break-words">
                      {getScript(widget).trim()}
                    </pre>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSettings(widget.id)}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant={widget.isActive ? 'destructive' : 'default'}
                      size="sm"
                      onClick={() => handleToggleStatus(widget)}
                      disabled={updatingWidgetId === widget.id}
                    >
                      {updatingWidgetId === widget.id ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : null}
                      {widget.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => handleOpenChat(widget.key)}
                      title={`https://demo.findecor.io/${widget.key}`}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      View Demo
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeletingWidgetId(widget.id)}
                    className="text-destructive hover:text-destructive"
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <DeleteConfirmationModal
                    open={deletingWidgetId === widget.id}
                    onOpenChange={(open) => !open && setDeletingWidgetId(null)}
                    onConfirm={handleDeleteWidget}
                    isLoading={isDeleting}
                    title="Delete Chat Widget"
                    description={
                      <span className="block space-y-2">
                        <span className="block">
                          Are you sure you want to delete this chat widget?
                        </span>
                        <span className="block font-medium mt-2">
                          Widget:{' '}
                          {widget.companyName ||
                            widget.name ||
                            `Widget #${widget.id}`}
                        </span>
                        <span className="block text-destructive mt-2">
                          This action cannot be undone. All related data will be
                          permanently removed.
                        </span>
                      </span>
                    }
                    confirmText="Delete Widget"
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatIntegration;
